import { useEffect, useState } from 'react';
import { getConfig, getFeatureStatus } from '../config';
import { MessageBus, Event } from '@podium/browser';

import {
    AdObj,
    AdError,
    Status,
    Format,
    DeviceType,
    DefineTagOptions,
    Size,
    PageType,
    Vertical,
    Subvertical,
    NativeOptions,
    Keywords,
    Finn,
    BannerGlobal,
} from '../../shared/types/ast';

import { Placement, Size as NRMSize } from '../../shared/types/reklameMaster';
import { inview } from '../utils/lazy';
import { markAdAvailable, markTagLoaded } from '../utils/performance';
import { cachePlaceholderAd } from './cached-ad-placeholder';

let messageBus: MessageBus;

const devicePrefix = (deviceType: DeviceType) => {
    switch (deviceType) {
        case 'mobile':
            return 'wph';

        case 'tablet':
            return 'wtb';

        case 'desktop':
            return 'wde';
    }
};

function nativeOnly(
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
) {
    return (
        vertical === 'travel' &&
        subvertical === 'prepackage' &&
        pageType === 'object'
    );
}

function sizes(
    deviceType: DeviceType,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
    isTakeoverCapable = false,
): Size[] {
    // everyone wants native
    const sizes: Size[] = [[1, 1]];

    if (!nativeOnly(pageType, vertical, subvertical)) {
        if (deviceType === 'mobile') {
            sizes.unshift([300, 250], [320, 250], [320, 400]);
        } else {
            sizes.unshift([580, 400]);
        }
    }

    // takeover is temporarily  disabled on travel
    if (isTakeoverCapable && vertical !== 'travel') {
        sizes.unshift([3, 3], [1920, 1080]);
    }

    return sizes;
}

function formats(
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
): Format[] {
    if (nativeOnly(pageType, vertical, subvertical)) {
        return ['native'];
    }
    return ['banner', 'native', 'video'];
}

function processedNrmSizes(
    nrmSizes: NRMSize[],
    skyscraperOnly: boolean = false,
) {
    function arrayEquals(a: number[], b: number[]) {
        return (
            Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((val, index) => val === b[index])
        );
    }
    const sizes = nrmSizes.map((size: NRMSize) => [size.width, size.height]);
    if (skyscraperOnly) {
        return sizes.filter((size) => !arrayEquals(size, [180, 700]));
    }
    return sizes;
}

export function tag(
    deviceType: DeviceType,
    instanceId: string,
    targetId: string,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
    skyscraperOnly?: boolean,
): DefineTagOptions | undefined {
    let config = getConfig();
    const reklameConfiguration = config?.reklameConfiguration;

    const enableNRM = getFeatureStatus('enableNRM');
    //@ts-ignore
    let bannerUrlTag;
    let nrmTag;
    if (enableNRM) {
        const placementConfig = reklameConfiguration?.placements?.find(
            (p: Placement) => p.placementId === instanceId,
        );

        nrmTag = !placementConfig?.inventoryCode
            ? undefined //a way of  filtering nulls
            : {
                  targetId,
                  sizes: processedNrmSizes(
                      placementConfig?.sizes,
                      skyscraperOnly,
                  ),
                  keywords: placementConfig?.keywords as Keywords,
                  invCode: placementConfig?.inventoryCode,
                  allowedFormats: placementConfig?.allowedFormats,
                  native: placementConfig?.native,
              };
    }
    const market = vertical === 'car' ? '_motor' : '';
    switch (instanceId) {
        case 'topboard':
            switch (deviceType) {
                case 'desktop':
                    bannerUrlTag = {
                        targetId,
                        sizes: [
                            [980, 150],
                            [1010, 150],
                            [728, 90],
                            [928, 120],
                        ],
                        keywords: {
                            'no-sno-adformat': 'topboard',
                        },
                        invCode: `no-finn-wde-${pageType}${market}_topboard`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'tablet':
                    bannerUrlTag = {
                        targetId,
                        sizes: [
                            [768, 150],
                            [728, 90],
                        ],
                        keywords: {
                            'no-sno-adformat': 'topboard',
                        },
                        invCode: `no-finn-wtb-${pageType}${market}_topboard`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'mobile':
                    break;
                default:
                    break;
            }
            break;
        case 'skyscraperright_1':
            switch (deviceType) {
                case 'desktop':
                    bannerUrlTag = {
                        targetId,
                        sizes: skyscraperOnly
                            ? [
                                  [160, 600],
                                  [180, 500],
                                  [4, 4],
                                  [300, 600],
                              ]
                            : [
                                  [160, 600],
                                  [180, 500],
                                  [180, 700],
                                  [4, 4],
                                  [300, 600],
                              ],

                        keywords: {
                            'no-sno-adformat': 'skyscraperright_1',
                        },
                        invCode: `no-finn-wde-${pageType}${market}_skyscraperright_1`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'tablet':
                case 'mobile':
                    break;
                default:
                    break;
            }
            break;
        case 'wallpaper':
            switch (deviceType) {
                case 'desktop':
                    bannerUrlTag = {
                        targetId,
                        sizes: [[2, 2]],
                        keywords: {
                            'no-sno-adformat': 'wallpaper',
                        },
                        invCode: `no-finn-wde-${pageType}${market}_wallpaper`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'tablet':
                case 'mobile':
                    break;
                default:
                    break;
            }
            break;
        case 'skyscraperleft_1':
            switch (deviceType) {
                case 'desktop':
                    bannerUrlTag = {
                        targetId,
                        sizes: [[180, 701]],
                        keywords: {
                            'no-sno-adformat': 'skyscraperleft_1',
                        },
                        invCode: `no-finn-wde-${pageType}${market}_skyscraperleft_1`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'tablet':
                case 'mobile':
                    break;
                default:
                    break;
            }
            break;
        case 'contentboard':
            bannerUrlTag = {
                targetId,
                sizes: [[1, 1]],
                keywords: {
                    'no-sno-adformat': instanceId,
                },
                invCode: `no-finn-${devicePrefix(
                    deviceType,
                )}-${pageType}${market}_${instanceId}`,
                allowedFormats: ['native'],
            };
            break;
        case 'test_1':
            switch (deviceType) {
                case 'mobile':
                    bannerUrlTag = {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: { 'no-sno-adformat': 'test_1' },
                        invCode: `no-finn-wph-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'tablet':
                    bannerUrlTag = {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: {
                            'no-sno-adformat': 'test_1',
                        },
                        invCode: `no-finn-wtb-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                    break;
                case 'desktop':
                    bannerUrlTag = {
                        targetId: 'test_1',
                        sizes: [[1, 2]],
                        keywords: { 'no-sno-adformat': 'test_1' },
                        invCode: `no-finn-wde-${pageType}${market}_test_1`,
                        allowedFormats: ['banner'],
                    };
                    break;
                default:
                    bannerUrlTag = {};
                    break;
            }
            break;
        default:
            bannerUrlTag = {
                invCode: `no-finn-${devicePrefix(
                    deviceType,
                )}-${pageType}${market}_${instanceId}`,
                sizes: sizes(
                    deviceType,
                    pageType,
                    vertical,
                    subvertical,
                    instanceId === 'board_2',
                ),
                allowedFormats: formats(pageType, vertical, subvertical),
                targetId,
                keywords: {
                    'aa-sch-ssa-format': 'NATIVE_CONTENT',
                    'no-sno-adformat': instanceId,
                },
                native: nativeLayout(instanceId),
            };
            break;
    }

    if (enableNRM) {
        if (bannerUrlTag) {
            //@ts-ignore
            bannerUrlTag['native'] = bannerUrlTag['native'] || null;
        }

        return nrmTag as DefineTagOptions;
    } else {
        return bannerUrlTag as DefineTagOptions;
    }
}

const nativeLayout = (instanceId: string) => {
    let native: Partial<NativeOptions> = {
        title: { required: true },
        clickUrl: { required: true },
        body: { required: false },
        description: { required: false },
        sponsoredBy: { required: false },
        image: { required: true },
        icon: { required: false },
        price: { required: false },
        cta: { required: false },
        desc2: { required: false },
    };

    if (['netboard_1', 'board_1'].includes(instanceId)) {
        native.video = { required: false };
    }

    return native;
};

const defineTag = (tagConfig?: DefineTagOptions) => {
    if (tagConfig) {
        if (window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.defineTag(tagConfig);
                }
            });
        }
        return tagConfig;
    }
    return;
};

function initialTargetId(id: string) {
    if (!messageBus) {
        messageBus = new MessageBus();
    }
    const existingCycle = messageBus.peek('banner', 'refresh');
    if (existingCycle) {
        const cycle = existingCycle.payload;
        return id + cycle;
    }
    return id;
}

export function useAd(
    id: string,
    deviceType: DeviceType,
    pageType?: PageType,
    vertical?: Vertical,
    subvertical?: Subvertical,
    skyscraperOnly?: boolean,
) {
    const [loaded, setLoaded] = useState(false);
    const [status, setStatus] = useState<Status>('requested');
    const [adObj2, setAdObj] = useState<AdObj>();
    const [error, setError] = useState<AdError>();
    const [targetId, setTargetId] = useState(initialTargetId(id));
    const tagConfig = tag(
        deviceType,
        id,
        targetId,
        pageType,
        vertical,
        subvertical,
        skyscraperOnly,
    );
    const sizes = tagConfig?.sizes;

    useEffect(() => {
        if (typeof window === 'object') {
            window.FINN = window.FINN || ({} as Finn);
            window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
            window.FINN.banner.queue = window.FINN.banner.queue || [];
            window.FINN.banner.queue.push(() => {
                if (window.apntag) {
                    window.apntag.onEvent('adRequested', targetId, () => {
                        setStatus('requested');
                    });

                    window.apntag.onEvent('adAvailable', targetId, (adObj) => {
                        markAdAvailable(id);
                        setAdObj(adObj);
                        setLoaded(true);
                        setStatus('available');
                    });

                    window.apntag.onEvent('adLoaded', targetId, (adObj) => {
                        setAdObj(adObj);
                        setStatus('loaded');
                        // Test for board placements in search results and cache it

                        const isNativeOrBannerAd =
                            adObj.adType === 'native' ||
                            adObj.adType === 'banner';

                        if (targetId.includes('board_') && isNativeOrBannerAd) {
                            setTimeout(() => {
                                const element =
                                    document.getElementById(targetId);
                                cachePlaceholderAd(
                                    targetId,
                                    element,
                                    adObj.adType,
                                );
                            }, 1000);
                        }
                    });

                    window.apntag.onEvent('adNoBid', targetId, () => {
                        setLoaded(true);
                        setStatus('failed');
                    });

                    window.apntag.onEvent('adBadRequest', targetId, (error) => {
                        setLoaded(true);
                        setError(error);
                        setStatus('failed');
                    });
                }

                const activeTag = defineTag(tagConfig);
                if (activeTag) {
                    markTagLoaded(id);
                    const { debouncedLoadTags } = window.FINN.banner;
                    if (debouncedLoadTags) {
                        debouncedLoadTags();
                    }
                }
            });
        } else {
            setLoaded(true);
            setStatus('failed');
        }
        if (!messageBus) {
            messageBus = new MessageBus();
        }

        function refreshCallback(event: Event) {
            const cycle = event.payload;
            if (cycle) {
                setTargetId(id + cycle);
            }
        }

        messageBus.subscribe('banner', 'refresh', refreshCallback);
        return function cleanup() {
            // clearAdPlaceholderCache();
            setAdObj(undefined);
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.offEvent('adAvailable', targetId);
                        window.apntag.offEvent('adLoaded', targetId);
                        window.apntag.offEvent('adNoBid', targetId);
                        window.apntag.offEvent('adBadRequest', targetId);
                    }
                });
            }
            messageBus.unsubscribe('banner', 'refresh', refreshCallback);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]); // We only want this to run when targetId changes

    return {
        adObj: adObj2,
        loaded,
        error,
        status,
        targetId,
        sizes,
    };
}

export function useShowTag(id: string, proximity = 100) {
    useEffect(() => {
        const { apntag } = window;
        let cleanup = () => {};
        if (apntag && apntag.anq) {
            const loadElement = (ele: Element) => {
                const tagId = ele.getAttribute('id');
                if (tagId) {
                    // Loads search result ad
                    apntag.anq.push(() => apntag.showTag(tagId));
                }
            };

            apntag.anq.push(() => {
                const element = document.getElementById(id);
                if (element) {
                    cleanup = inview(element, loadElement, proximity);
                }
            });
        }
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]); // We only want this to run when id changes
    return {};
}
